import { useState } from "react";
import { supabase } from "./supabase";
import { B } from "./constants";
import ZeroKnowledgeWarning from "./components/ZeroKnowledgeWarning.jsx";

export default function LoginScreen({ onAuth }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [waitlist, setWaitlist] = useState(false);
  const [showZkWarning, setShowZkWarning] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Invite code validation on signup
    if (isSignUp) {
      if (!inviteCode.trim()) {
        setError("Invite code is required.");
        setLoading(false);
        return;
      }
      try {
        const invRes = await fetch("/api/misc?action=validate-invite", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: inviteCode.trim() }),
        });
        const invData = await invRes.json();
        if (!invData.valid) {
          setWaitlist(true);
          setLoading(false);
          return;
        }
        // Store invite_id for post-signup redemption
        var validInviteId = invData.invite_id;
      } catch {
        setError("Could not validate invite code. Try again.");
        setLoading(false);
        return;
      }
    }

    try {
      const { data, error: authErr } = isSignUp
        ? await supabase.auth.signUp({ email, password })
        : await supabase.auth.signInWithPassword({ email, password });
      if (authErr) { setError(authErr.message); setLoading(false); return; }
      if (isSignUp && data?.user && !data.session) {
        setError("Check your email to confirm your account.");
        setLoading(false);
        return;
      }

      // Redeem invite after successful signup
      if (isSignUp && data?.session && validInviteId) {
        fetch("/api/misc?action=redeem-invite", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${data.session.access_token}` },
          body: JSON.stringify({ invite_id: validInviteId }),
        }).catch((err) => console.error('[LoginScreen:handleSubmit] invite redeem failed:', err)); // non-blocking
      }

      onAuth(data.session);
    } catch (err) {
      setError("Something went wrong. Try again.");
    }
    setLoading(false);
  };

  const executePasswordReset = async () => {
    setShowZkWarning(false);
    setLoading(true);
    const{error:resetErr}=await supabase.auth.resetPasswordForEmail(email,{redirectTo:window.location.origin});
    setLoading(false);
    if(resetErr){setError(resetErr.message);}else{setResetSent(true);}
  };

  if (showZkWarning) {
    return <ZeroKnowledgeWarning onProceed={executePasswordReset} onCancel={() => setShowZkWarning(false)} />;
  }

  return (
    <div style={{minHeight:"100vh",background:B.bg,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,fontFamily:"IBM Plex Mono,monospace"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@400;500&display=swap');*{box-sizing:border-box;margin:0;padding:0}input::placeholder{color:${B.muted}}@keyframes fadeUp{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}.f{animation:fadeUp 0.2s ease forwards}`}</style>
      <div style={{marginBottom:32,textAlign:"center"}}>
        <div style={{fontSize:11,letterSpacing:"0.2em",color:B.accent,textTransform:"uppercase",fontWeight:600,marginBottom:8}}>FYNWELL</div>
        <h1 style={{fontSize:22,fontWeight:600,color:B.text,margin:0}}>Your Financial Intelligence</h1>
      </div>
      {waitlist ? (
        <div style={{width:"100%",maxWidth:320,textAlign:"center"}}>
          <div style={{fontSize:14,color:B.text,marginBottom:8,fontFamily:"IBM Plex Sans,sans-serif"}}>
            Fynwell is in private beta.
          </div>
          <div style={{fontSize:13,color:B.textSec,marginBottom:20,fontFamily:"IBM Plex Sans,sans-serif",lineHeight:1.5}}>
            Join the waitlist and we'll let you know when a spot opens up.
          </div>
          <a
            href={`mailto:waitlist@fynwell.ai?subject=Waitlist%20request&body=Email:%20${encodeURIComponent(email)}`}
            style={{
              display:"inline-block",padding:"12px 24px",borderRadius:10,background:B.accent,
              color:"#0A0E14",fontSize:13,fontWeight:600,textDecoration:"none",fontFamily:"IBM Plex Mono,monospace",
            }}
          >
            Join Waitlist
          </a>
          <button type="button" onClick={()=>{setWaitlist(false);setError("");}}
            style={{display:"block",margin:"16px auto 0",background:"none",border:"none",color:B.muted,fontSize:11,cursor:"pointer",fontFamily:"IBM Plex Sans,sans-serif"}}>
            Try a different code
          </button>
        </div>
      ) : (
      <form onSubmit={submit} aria-label={isSignUp?"Create account":"Sign in"} style={{width:"100%",maxWidth:320,display:"flex",flexDirection:"column",gap:12}}>
        <label htmlFor="aria-email" style={{position:"absolute",width:1,height:1,overflow:"hidden",clip:"rect(0,0,0,0)"}}>Email</label>
        <input
          id="aria-email" type="email" placeholder="Email" value={email} onChange={e=>setEmail(e.target.value)} required autoComplete="email"
          style={{padding:"12px 14px",borderRadius:10,border:`1px solid ${B.border}`,background:"rgba(255,255,255,0.04)",color:B.text,fontSize:13,fontFamily:"IBM Plex Sans,sans-serif"}}
        />
        <label htmlFor="aria-password" style={{position:"absolute",width:1,height:1,overflow:"hidden",clip:"rect(0,0,0,0)"}}>Password</label>
        <input
          id="aria-password" type="password" placeholder="Password" value={password} onChange={e=>setPassword(e.target.value)} required minLength={6} autoComplete={isSignUp?"new-password":"current-password"}
          style={{padding:"12px 14px",borderRadius:10,border:`1px solid ${B.border}`,background:"rgba(255,255,255,0.04)",color:B.text,fontSize:13,fontFamily:"IBM Plex Sans,sans-serif"}}
        />
        {isSignUp && (
          <>
            <label htmlFor="aria-invite" style={{position:"absolute",width:1,height:1,overflow:"hidden",clip:"rect(0,0,0,0)"}}>Invite code</label>
            <input
              id="aria-invite" type="text" placeholder="Invite code" value={inviteCode} onChange={e=>{setInviteCode(e.target.value.toUpperCase());setWaitlist(false);}} required autoComplete="off" spellCheck="false"
              style={{padding:"12px 14px",borderRadius:10,border:`1px solid ${B.border}`,background:"rgba(255,255,255,0.04)",color:B.text,fontSize:13,fontFamily:"IBM Plex Mono,monospace",letterSpacing:"0.1em"}}
            />
          </>
        )}
        {isSignUp && (
          <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:B.textSec,fontFamily:"IBM Plex Sans,sans-serif",cursor:"pointer"}}>
            <input type="checkbox" checked={ageConfirmed} onChange={e=>setAgeConfirmed(e.target.checked)} style={{accentColor:B.accent,width:16,height:16}} />
            I confirm I am 18 years of age or older
          </label>
        )}
        <button type="submit" disabled={loading || (isSignUp && !ageConfirmed)}
          style={{padding:"12px 14px",borderRadius:10,border:"none",background:B.accent,color:"#0A0E14",fontSize:13,fontWeight:600,cursor:(loading||(isSignUp&&!ageConfirmed))?"not-allowed":"pointer",fontFamily:"IBM Plex Mono,monospace",opacity:(loading||(isSignUp&&!ageConfirmed))?0.5:1}}>
          {loading?(isSignUp?"Creating account...":"Signing in..."):(isSignUp?"Create Account":"Sign In")}
        </button>
        {error&&<div role="alert" style={{fontSize:12,color:B.red,textAlign:"center"}}>{error}</div>}
        {resetSent&&<div role="status" style={{fontSize:12,color:B.green,textAlign:"center"}}>Check your email for a reset link</div>}
        {!isSignUp&&<button type="button" onClick={()=>{
          if(!email.trim()){setError("Enter your email first.");return;}
          setError("");setResetSent(false);setShowZkWarning(true);
        }} style={{background:"none",border:"none",color:B.cyan,fontSize:11,cursor:"pointer",fontFamily:"IBM Plex Sans,sans-serif"}}>
          Forgot password?
        </button>}
        <button type="button" onClick={()=>{setIsSignUp(!isSignUp);setError("");setResetSent(false);}}
          style={{background:"none",border:"none",color:B.muted,fontSize:11,cursor:"pointer",fontFamily:"IBM Plex Sans,sans-serif",marginTop:4}}>
          {isSignUp?"Already have an account? Sign in":"Need an account? Sign up"}
        </button>
      </form>
      )}
      <div style={{marginTop:32,fontSize:10,color:B.muted,textAlign:"center",fontFamily:"IBM Plex Sans,sans-serif"}}>
        <span>US only. </span>
        <a href="/privacy" style={{color:B.muted,textDecoration:"underline"}}>Privacy Policy</a>
        <span aria-hidden="true"> | </span>
        <a href="/terms" style={{color:B.muted,textDecoration:"underline"}}>Terms</a>
      </div>
    </div>
  );
}
